import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyledCountSection } from '@/src/components/common/eco/sections/counter/counter.style.js';
import { Col, Container, Row } from 'react-bootstrap';
import CountUp from 'react-countup';
import { gsap } from 'gsap';
import useContainerOffset from '@/src/hooks/useContainerOffset.jsx';
import reactHtmlParser from 'react-html-parser';
import { ImgParallax } from '@/src/components/common/ui/images/ImgParallax.jsx';
import LinkOnly from '@/src/components/common/ui/buttons/LinkOnly.jsx';
import SubTitle from '@/src/components/common/ui/text/SubTitle.jsx';
import ListItemCounter from '@/src/components/common/ui/single-item/ListItemCounter.jsx';
import Modal from 'react-bootstrap/Modal';
import ModalSlider from '@/src/components/common/eco/sections/modal-slider/ModalSlider.jsx';
import useDeviceTypeMobile from '@/src/hooks/useDeviceTypeMobile.jsx';

const CountSectionCommon = ({data, id}) => {
	const offset = useContainerOffset('.container');
	const containerRef = useRef(null);
	const containerCounterRef = useRef([]);
	const countUpStart = useRef(null);
	const startRefs = useRef([]);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [modalIsOpenData, setModalIsOpenData] = useState(''); // Added modalIsOpenData
	const isMobile = useDeviceTypeMobile()
	const [isAppleDevice, setIsAppleDevice] = useState(false);

	// Memoize the animation setup to prevent unnecessary re-renders
	const setupAnimations = useCallback(() => {
		const counterAnim = document.querySelectorAll('.text3 span');
		const $text3 = document.querySelector(".text3");
		const counterAnimBold = document.querySelectorAll('.text4 span');
		const $text4 = document.querySelector(".text4");

		// Cleanup previous animations
		gsap.killTweensOf([...counterAnim, ...counterAnimBold]);

		// Setup animations for text3
		counterAnim.forEach((el, index) => {
			gsap.fromTo(el, {
				autoAlpha: 0,
				y: 50 * (2 * index),
				ease: "none",
				duration: 1.3,
			}, {
				y: 0,
				autoAlpha: 1,
				ease: "power2",
				duration: 1.3,
				stagger: {
					id: `${index + 1}`,
					each: 0.5,
					repeatRefresh: true,
				},
				scrollTrigger: {
					id: `${index + 1}`,
					trigger: $text3,
					toggleActions: 'play none none reverse',
				}
			});
		});

		// Setup animations for text4
		counterAnimBold.forEach((el, index) => {
			gsap.fromTo(el, {
				autoAlpha: 0,
				y: 50 * (2 * index),
				ease: "none",
				duration: 1.3,
			}, {
				y: 0,
				autoAlpha: 1,
				ease: "power2",
				duration: 1.3,
				stagger: {
					id: `${index + 1}`,
					each: 1,
					repeatRefresh: true,
				},
				scrollTrigger: {
					id: `${index + 1}`,
					trigger: $text4,
					toggleActions: 'play none none reverse',
				}
			});
		});
	}, []);

	// Setup main counter animation
	useEffect(() => {
		setupAnimations();

		return () => {
			// Cleanup GSAP animations
			gsap.killTweensOf('.text3 span, .text4 span');
		};
	}, [setupAnimations]);

	// Setup intersection observer for main counter
	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					countUpStart.current?.start();
				}
			},
			{ threshold: 0.5 }
		);

		if (containerRef.current) {
			observer.observe(containerRef.current);
		}

		return () => observer.disconnect();
	}, []);

	// Setup intersection observers for individual counters
	useEffect(() => {
		const observers = [];

		containerCounterRef.current.forEach((ref, index) => {
			if (!ref) return;

			const observer = new IntersectionObserver(
				([entry]) => {
					if (entry.isIntersecting) {
						startRefs.current[index]?.();
					}
				},
				{ threshold: 0.5 }
			);

			observer.observe(ref);
			observers.push(observer);
		});

		return () => observers.forEach((observer) => observer.disconnect());
	}, [data?.posts?.list]);

	// Memoize the main counter value
	const mainCounterValue = useMemo(() => ({
		start: data?.section_data?.short_desc ? data?.section_data?.short_desc - data?.section_data?.short_desc : 0,
		end: data?.section_data?.short_desc || 0
	}), [data?.section_data?.short_desc]);

	// Memoize the counter items
	const counterItems = useMemo(() => (
		data?.posts?.list?.map((e, index) => ({
			start: e?.data?.subtitle ? e?.data?.subtitle - 10 : 10801,
			end: e?.data?.subtitle || 11000,
			title: e?.data?.title,
			short_desc: e?.data?.short_desc,
			counter: e?.data?.type,
		}))
	), [data?.posts?.list]);


	const renderBrandItem = useMemo(() =>
			(item, index) => {
				return (
					<Col className="counter_bottom_single" md={6} key={index}>
						<ListItemCounter counter={item?.counter === '8' ? true : false} title={item?.short_desc} description={item.title}/>
					</Col>
				);
			},
		[],
	);

	useEffect(() => {
		const userAgent = window.navigator.userAgent.toLowerCase();
		setIsAppleDevice(/iphone|ipad|ipod|macintosh|mac os x/.test(userAgent));
	}, []);
	return (
		<StyledCountSection id={id || 'about-us'} offset={offset} className="counter">
			<Container fluid className="second_item">
				<Row className="counter_bottom">
					<Col md={{ span: 4 }}>
						{
							data?.section_data?.subtitle &&
							<SubTitle  marGin={'0 0 40px'} text={data?.section_data?.subtitle} />
						}

						<div className="content_large">

							{
								mainCounterValue.end &&
								<h3 ref={containerRef}  data-scroll data-scroll-speed="1">
									<CountUp
										enableScrollSpy={true}
										start={mainCounterValue.start}
										end={mainCounterValue.end}
										duration={2}
										separator=","
										decimals={0}
										decimal=","
									>
										{({ countUpRef: countRef, start }) => {
											countUpStart.current = { start };
											return <span ref={countRef} />;
										}}
									</CountUp>
									<div className="plus">+</div>
								</h3>

							}

							{data?.section_data?.description && reactHtmlParser(data?.section_data?.description)}


							{

								isAppleDevice ?
									data?.section_data?.button_text &&
									<div className="fade-up">
										<LinkOnly underline color={'white'} hoverbackground={'#7FB45A'} margin="40px 0 0" text={data?.section_data?.button_text} target={'_blank'} src={data?.section_data?.btn_link} />
									</div>
									:
									data?.section_data?.button_text &&
									<div className="fade-up" onClick={e => {
										e.preventDefault();
										setModalIsOpenData(data?.section_data?.btn_link);

										setModalIsOpen(true); // opens modal
									}}>
										<LinkOnly underline color={'white'} hoverbackground={'#7FB45A'} margin="40px 0 0" text={data?.section_data?.button_text} src={'javascript:void(0)'} />
									</div>

							}


						</div>
					</Col>
					{
						counterItems &&
						<Col md={{ span: 6, offset: 2 }}>
							<Row className={'custom-counter-row'}>
								{counterItems?.map((item, index) => renderBrandItem(item, index))}
							</Row>
						</Col>
					}

				</Row>
			</Container>
			<ImgParallax alt={'Counter Background'} src={isMobile ? '/images/static/counter-mb.jpg' :'/images/static/counter.jpg'}/>


			<Modal
				data={modalIsOpenData}
				className={'modal-video moda-eco-sourcing'}
				show={modalIsOpen}
				onclick={() => {
					setModalIsOpen(false);
				}}>
				<Modal.Body className={'video'}>
					<div style={{ height: "100vh" }}>
						<ModalSlider onclick={() => {
							setModalIsOpen(false);
						}} data={modalIsOpenData} pdf/>
					</div>
				</Modal.Body>
			</Modal>
		</StyledCountSection>
	);
};


export default React.memo(CountSectionCommon);
